import { useEffect, useRef, useState } from 'react';

const ENGINE_URL = '/stockfish/stockfish-18-lite-single.js';
const SEARCH_DEPTH = 12;

// Local Stockfish (lite single-threaded WASM) in a web worker.
// Lazily started when `enabled` flips true; evaluates `fen` whenever it
// changes. Scores are normalized to White's perspective.
// `valid=false` (impossible positions, e.g. after a sudo force-move)
// pauses evaluation — Stockfish misbehaves on illegal positions.
export default function useStockfish(enabled, fen, valid = true) {
  const [evaluation, setEvaluation] = useState(null); // { cp, mate, depth }
  const [status, setStatus] = useState('off'); // off | loading | ready | error
  const workerRef = useRef(null);
  const readyRef = useRef(false);
  const latestFenRef = useRef(fen);
  latestFenRef.current = fen;

  // Both kings must be on the board (and the position legal) to search.
  const evaluable =
    valid && /k/.test(fen.split(' ')[0]) && /K/.test(fen.split(' ')[0]);

  useEffect(() => {
    if (!enabled || workerRef.current) return;
    setStatus('loading');
    let worker;
    try {
      worker = new Worker(ENGINE_URL);
    } catch {
      setStatus('error');
      return;
    }
    workerRef.current = worker;

    worker.onmessage = (e) => {
      const line = typeof e.data === 'string' ? e.data : '';
      if (line === 'uciok') {
        worker.postMessage('setoption name Threads value 1');
        worker.postMessage('isready');
      } else if (line === 'readyok') {
        readyRef.current = true;
        setStatus('ready');
        requestEval(worker, latestFenRef.current);
      } else if (line.startsWith('info ') && line.includes(' score ')) {
        const depthMatch = line.match(/\bdepth (\d+)/);
        const cpMatch = line.match(/\bscore cp (-?\d+)/);
        const mateMatch = line.match(/\bscore mate (-?\d+)/);
        const depth = depthMatch ? parseInt(depthMatch[1], 10) : 0;
        // Scores come from the side to move; flip for Black so the bar
        // is always from White's perspective.
        const sign = latestFenRef.current.split(' ')[1] === 'b' ? -1 : 1;
        if (mateMatch) {
          setEvaluation({ cp: null, mate: sign * parseInt(mateMatch[1], 10), depth });
        } else if (cpMatch) {
          setEvaluation({ cp: sign * parseInt(cpMatch[1], 10), mate: null, depth });
        }
      }
    };
    worker.onerror = () => setStatus('error');
    worker.postMessage('uci');
  }, [enabled]);

  // Re-evaluate when the position changes (debounced).
  useEffect(() => {
    if (!enabled || !readyRef.current || !workerRef.current) return;
    if (!evaluable) {
      setEvaluation(null);
      return;
    }
    const timer = setTimeout(() => {
      requestEval(workerRef.current, fen);
    }, 250);
    return () => clearTimeout(timer);
  }, [fen, enabled, evaluable]);

  // Terminate on unmount.
  useEffect(() => {
    return () => {
      if (workerRef.current) {
        workerRef.current.terminate();
        workerRef.current = null;
        readyRef.current = false;
      }
    };
  }, []);

  return { evaluation: enabled && evaluable ? evaluation : null, status };
}

function requestEval(worker, fen) {
  worker.postMessage('stop');
  worker.postMessage(`position fen ${fen}`);
  worker.postMessage(`go depth ${SEARCH_DEPTH}`);
}
